import React, { useState } from 'react';
import { SettingTahunAjaran } from '../../types/index.js';
import { Edit2, Save, CheckCircle2, ShieldCheck, Building2, Monitor, Smartphone, QrCode, Code2 } from 'lucide-react';

interface SettingTahunAjaranViewProps {
  setting: SettingTahunAjaran;
  onUpdateSetting: (data: Partial<SettingTahunAjaran>) => Promise<void>;
  appMode?: 'WEB_PORTAL' | 'EXPO_SIMULATOR' | 'GATEWAY_TESTER' | 'EXPO_CODE';
  setAppMode?: (mode: 'WEB_PORTAL' | 'EXPO_SIMULATOR' | 'GATEWAY_TESTER' | 'EXPO_CODE') => void;
}

export const SettingTahunAjaranView: React.FC<SettingTahunAjaranViewProps> = ({
  setting,
  onUpdateSetting,
  appMode = 'WEB_PORTAL',
  setAppMode,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ ...setting });

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    await onUpdateSetting(formData);
    setIsEditing(false);
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-200">


      {/* Header */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-bold text-slate-900">Setting Tahun Ajaran & Profil Lembaga</h2>
          <p className="text-xs text-slate-500">
            Konfigurasi identitas unit sekolah, prefix Virtual Account bank, dan template cetak kuitansi
          </p>
        </div>

        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-1.5 bg-sky-600 hover:bg-sky-500 text-white text-xs font-semibold px-3.5 py-1.5 rounded-md transition shadow-xs"
          >
            <Edit2 className="w-3.5 h-3.5" />
            <span>Edit Setting</span>
          </button>
        ) : (
          <button
            onClick={handleSave}
            className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold px-3.5 py-1.5 rounded-md transition shadow-xs"
          >
            <Save className="w-3.5 h-3.5" />
            <span>Simpan Perubahan</span>
          </button>
        )}
      </div>

      {/* Table Exact from Document Page 9 Bottom */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold uppercase tracking-wider">
              <tr>
                <th className="py-2.5 px-4 w-24">Aksi</th>
                <th className="py-2.5 px-4">Unit</th>
                <th className="py-2.5 px-4">Nama Sekolah</th>
                <th className="py-2.5 px-4">Alamat</th>
                <th className="py-2.5 px-4">Kota</th>
                <th className="py-2.5 px-4 font-mono">Prefix VA</th>
                <th className="py-2.5 px-4">Versi Print</th>
                <th className="py-2.5 px-4 text-center">Tahun Ajaran</th>
                <th className="py-2.5 px-4 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <tr className="hover:bg-sky-50/50">
                <td className="py-2.5 px-4">
                  <button
                    onClick={() => setIsEditing(!isEditing)}
                    className="px-2 py-0.5 text-[11px] font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded border border-emerald-300 flex items-center gap-1"
                  >
                    <Edit2 className="w-3 h-3" />
                    <span>Edit</span>
                  </button>
                </td>
                <td className="py-2.5 px-4 font-mono font-bold text-sky-800">{formData.unit}</td>
                <td className="py-2.5 px-4 font-bold text-slate-900">{formData.namaSekolah}</td>
                <td className="py-2.5 px-4 text-slate-600">{formData.alamat}</td>
                <td className="py-2.5 px-4 text-slate-700">{formData.kota}</td>
                <td className="py-2.5 px-4 font-mono font-bold text-sky-800 tracking-wider">
                  {formData.prefixVA}
                </td>
                <td className="py-2.5 px-4 text-slate-600">{formData.versiPrint}</td>
                <td className="py-2.5 px-4 text-center font-mono font-semibold text-slate-800">
                  {formData.tahunAjaranAktif}
                </td>
                <td className="py-2.5 px-4 text-center">
                  <span className="bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded-full text-[10px]">
                    {formData.status}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {isEditing && (
        <form onSubmit={handleSave} className="bg-white p-5 rounded-xl border border-sky-200 shadow-sm space-y-4 text-xs">
          <h3 className="font-bold text-sm text-sky-900 border-b pb-2">Form Perubahan Pengaturan</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-slate-700 font-semibold mb-1">Kode Unit</label>
              <input
                type="text"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                className="w-full px-3 py-2 border rounded-md font-mono"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-slate-700 font-semibold mb-1">Nama Lembaga / Sekolah</label>
              <input
                type="text"
                value={formData.namaSekolah}
                onChange={(e) => setFormData({ ...formData, namaSekolah: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label className="block text-slate-700 font-semibold mb-1">Alamat Lengkap</label>
              <input
                type="text"
                value={formData.alamat}
                onChange={(e) => setFormData({ ...formData, alamat: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-1">Kota / Kabupaten</label>
              <input
                type="text"
                value={formData.kota}
                onChange={(e) => setFormData({ ...formData, kota: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-slate-700 font-semibold mb-1">Prefix Nomor Virtual Account</label>
              <input
                type="text"
                value={formData.prefixVA}
                onChange={(e) => setFormData({ ...formData, prefixVA: e.target.value })}
                className="w-full px-3 py-2 border rounded-md font-mono"
              />
              <span className="text-[10px] text-slate-400">Digunakan oleh Bank Muamalat, BNI, Duitku, dll.</span>
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-1">Versi Print Kuitansi</label>
              <input
                type="text"
                value={formData.versiPrint}
                onChange={(e) => setFormData({ ...formData, versiPrint: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-1">Tahun Ajaran Aktif</label>
              <input
                type="text"
                value={formData.tahunAjaranAktif}
                onChange={(e) => setFormData({ ...formData, tahunAjaranAktif: e.target.value })}
                className="w-full px-3 py-2 border rounded-md font-mono"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="px-4 py-1.5 bg-slate-100 text-slate-600 rounded-md font-medium"
            >
              Batal
            </button>
            <button
              type="submit"
              className="px-5 py-1.5 bg-sky-600 text-white rounded-md font-semibold hover:bg-sky-500"
            >
              Simpan Pengaturan
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
